import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
    MdCheckCircle,
    MdErrorOutline,
    MdInfoOutline,
    MdWarningAmber,
    MdClose,
} from 'react-icons/md';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
    id: number;
    message: string;
    variant: ToastVariant;
}

interface ToastContextValue {
    show: (message: string, variant?: ToastVariant, durationMs?: number) => void;
    success: (message: string, durationMs?: number) => void;
    error: (message: string, durationMs?: number) => void;
    info: (message: string, durationMs?: number) => void;
    warning: (message: string, durationMs?: number) => void;
    dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const VARIANT_STYLES: Record<ToastVariant, { bar: string; icon: ReactNode }> = {
    success: { bar: 'bg-green-500', icon: <MdCheckCircle size={20} className="text-green-500" /> },
    error: { bar: 'bg-red-500', icon: <MdErrorOutline size={20} className="text-red-500" /> },
    info: { bar: 'bg-blue-500', icon: <MdInfoOutline size={20} className="text-blue-500" /> },
    warning: { bar: 'bg-amber-500', icon: <MdWarningAmber size={20} className="text-amber-500" /> },
};

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    const dismiss = useCallback((id: number) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const show = useCallback(
        (message: string, variant: ToastVariant = 'info', durationMs = 4000) => {
            const id = nextId++;
            setToasts((prev) => [...prev, { id, message, variant }]);
            if (durationMs > 0) {
                window.setTimeout(() => dismiss(id), durationMs);
            }
        },
        [dismiss],
    );

    const value = useMemo<ToastContextValue>(
        () => ({
            show,
            success: (msg, d) => show(msg, 'success', d),
            error: (msg, d) => show(msg, 'error', d),
            info: (msg, d) => show(msg, 'info', d),
            warning: (msg, d) => show(msg, 'warning', d),
            dismiss,
        }),
        [show, dismiss],
    );

    return (
        <ToastContext.Provider value={value}>
            {children}
            {typeof document !== 'undefined' &&
                createPortal(
                    <div className="pointer-events-none fixed right-4 top-4 z-[999] flex w-full max-w-sm flex-col gap-3 sm:right-6 sm:top-6">
                        {toasts.map((t) => (
                            <ToastCard key={t.id} toast={t} onClose={() => dismiss(t.id)} />
                        ))}
                    </div>,
                    document.body,
                )}
        </ToastContext.Provider>
    );
}

function ToastCard({ toast, onClose }: { toast: ToastItem; onClose: () => void }) {
    const style = VARIANT_STYLES[toast.variant];

    return (
        <div className="pointer-events-auto flex w-full animate-toast-slide-in overflow-hidden rounded-xl border border-[#eee] bg-white shadow-lg">
            <div className={`w-1 shrink-0 ${style.bar}`} />
            <div className="flex flex-1 items-start gap-3 px-4 py-3">
                <span className="mt-0.5 shrink-0">{style.icon}</span>
                <p className="flex-1 text-[14px] leading-snug text-secondary">{toast.message}</p>
                <button
                    type="button"
                    onClick={onClose}
                    title="Dismiss"
                    className="shrink-0 rounded-full p-1 text-[#999] transition-colors hover:bg-[#f5f5f5] hover:text-secondary"
                >
                    <MdClose size={16} />
                </button>
            </div>
        </div>
    );
}

export function useToast(): ToastContextValue {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within ToastProvider');
    return ctx;
}
